import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/Spinner";
import http from "../../api/apiClient";

type Mode = "PARENT" | "FAMILY";

const ENDPOINT = "/finance/settings/payer-name";

/**
 * Which name a linked payment shows when e-Boekhouden gives no payer: the parent whose bank account it came from, or
 * the family. Unlinked payments always show the account holder from the bank line.
 */
export default function PayerNameSetting({ canManage }: { canManage: boolean }): ReactElement {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    http.get(ENDPOINT)
      .then(({ data }) => {
        if (!cancelled) setMode(data?.mode === "FAMILY" ? "FAMILY" : "PARENT");
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (next: Mode) => {
    if (saving || next === mode) return;
    const previous = mode;
    setMode(next);
    setSaving(true);
    setError(false);
    try {
      const { data } = await http.put(ENDPOINT, { mode: next });
      setMode(data?.mode === "FAMILY" ? "FAMILY" : "PARENT");
    } catch {
      setMode(previous);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const options: Array<{ value: Mode; label: string; example: string }> = [
    {
      value: "PARENT",
      label: t("finance.settings.payer_name.parent", { defaultValue: "The parent whose bank account it came from" }),
      example: t("finance.settings.payer_name.parent_example", { defaultValue: "For example \"Michelle van Elk\"; the family when the account is not known." }),
    },
    {
      value: "FAMILY",
      label: t("finance.settings.payer_name.family", { defaultValue: "The family" }),
      example: t("finance.settings.payer_name.family_example", { defaultValue: "For example \"Family van Elk\"." }),
    },
  ];

  return (
    <section data-payer-name-setting={mode ?? ""} className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
      <h2 className="text-base font-bold text-slate-950 dark:text-white">
        {t("finance.settings.payer_name.title", { defaultValue: "Name of the payer" })}
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {t("finance.settings.payer_name.help", {
          defaultValue: "e-Boekhouden does not always say who paid. A linked payment then shows the name chosen here; a payment that is not linked yet shows the account holder from the bank line.",
        })}
      </p>
      {loading ? (
        <Spinner />
      ) : (
        <fieldset className="mt-3 grid gap-2">
          <legend className="sr-only">{t("finance.settings.payer_name.title", { defaultValue: "Name of the payer" })}</legend>
          {options.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm text-slate-800 dark:text-slate-100">
              <input
                type="radio"
                name="payer-name-mode"
                value={option.value}
                checked={mode === option.value}
                disabled={!canManage || saving}
                onChange={() => save(option.value)}
                className="mt-1"
              />
              <span>
                <span className="font-semibold">{option.label}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">{option.example}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs font-semibold text-rose-600">
          {t("finance.settings.payer_name.save_failed", { defaultValue: "Saving failed. Please try again." })}
        </p>
      )}
    </section>
  );
}
